/*
  <ia-ga4-events> — the GA4 dataLayer bridge that GTM (GTM-WRZB4SV) reads.
  Rendered by snippets/ia-ga4-events.liquid at the end of <body>.

  Event and parameter names are the contract with the GTM container, carried
  over from the legacy theme unchanged. The sources are this theme's bus
  events, never DOM selectors:
    view_item                      product page (the JSON inside this element)
                                   and the bundle-builder page (its parent)
    add_to_cart / remove_from_cart the difference between the cart before and
                                   after one of our own writes — every add
                                   path (PDP, quick buy, card, upsell, bundle)
    view_cart                      the cart drawer opening (ia-drawer-opened)
    begin_checkout                 the drawer's checkout submit (ia-cart:checkout)
    view_product_image             pdp:media-change
    image_comparison_interaction   before-after:change

  Config: data-currency (presentment currency), data-affiliation,
  data-cart-drawer (the drawer id that means "the cart").
*/
import { EVENTS, subscribe } from 'ia-util-pubsub'
import { countsTowardBag, isGrantedLine } from 'ia-cart-api'

// Cents to a two-decimal major unit, the shape GA4 wants for price and value.
const toMajor = (cents) => Number((cents / 100).toFixed(2))

// A line the shopper chose to buy: the theme's own bookkeeping lines and the
// granted (free) ones are not purchases GA4 should count.
const isTracked = (item) => countsTowardBag(item) && !isGrantedLine(item)

class IaGa4Events extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || window.Shopify?.currency?.active
    this.affiliation = this.dataset.affiliation || 'Online Store'
    this.cartDrawerId = this.dataset.cartDrawer || 'cart'
    this.cart = this.seedCart()
    this.mutating = false

    this.unsubscribers?.forEach((unsubscribe) => unsubscribe())
    this.unsubscribers = [
      // A refresh (bfcache return, an app's write) republishes the cart too;
      // only a change that announced itself first is a shopper action.
      subscribe(EVENTS.cartBeforeChange, () => { this.mutating = true }),
      subscribe(EVENTS.cartError, () => { this.mutating = false }),
      subscribe(EVENTS.cartChanged, this.handleCartChanged.bind(this)),
      subscribe(EVENTS.drawerOpened, this.handleDrawerOpened.bind(this)),
      subscribe(EVENTS.cartCheckout, this.handleCheckout.bind(this)),
      subscribe(EVENTS.pdpMediaChange, this.handleMediaChange.bind(this)),
      subscribe(EVENTS.beforeAfterChange, this.handleComparison.bind(this))
    ]

    this.viewProduct()
    this.viewBundle()
  }

  disconnectedCallback() {
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe())
    this.unsubscribers = null
  }

  push(data) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  }

  // `_clear: true` empties GTM's ecommerce object before the push lands, so
  // one event never inherits the items of the previous one.
  pushEcommerce(event, ecommerce) {
    this.push({ event, ecommerce: { currency: this.currency, ...ecommerce }, _clear: true })
  }

  seedCart() {
    try {
      return JSON.parse(document.getElementById('ia-cart-json')?.textContent || 'null')
    } catch (error) {
      return null
    }
  }

  trackedLines(cart) {
    return new Map((cart?.items || []).filter(isTracked).map((item) => [item.key, item]))
  }

  toItem(item, quantity) {
    const data = {
      item_id: String(item.product_id),
      item_name: item.product_title,
      item_brand: item.vendor,
      affiliation: this.affiliation,
      price: toMajor(item.final_price ?? item.price),
      quantity
    }
    if (item.variant_title && item.variant_title !== 'Default Title') data.item_variant = item.variant_title
    return data
  }

  lineItems(cart) {
    return [...this.trackedLines(cart).values()].map((item) => this.toItem(item, item.quantity))
  }

  /* ---- view_item ---------------------------------------------------------- */

  viewProduct() {
    const json = this.querySelector('[data-ga4-item]')?.textContent
    if (!json) return
    try {
      this.pushEcommerce('view_item', { items: [JSON.parse(json)] })
    } catch (error) {
      console.warn('[ia-ga4-events] view_item skipped, bad item JSON', error)
    }
  }

  // The bundle-builder page is a page template, not a product: the parent
  // product GA4 should see is what the builder itself carries.
  viewBundle() {
    const builder = document.querySelector('ia-bundle-builder')
    if (!builder?.dataset.parentId) return
    const { parentId, parentTitle, parentVendor } = builder.dataset
    this.pushEcommerce('view_item', {
      items: [{ item_id: parentId, item_name: parentTitle, item_brand: parentVendor, affiliation: this.affiliation }]
    })
  }

  /* ---- add_to_cart / remove_from_cart ------------------------------------- */

  handleCartChanged(event) {
    const { cartObj, changes } = event.detail || {}
    if (!cartObj?.items) return
    const previous = this.trackedLines(this.cart)
    const next = this.trackedLines(cartObj)
    this.cart = cartObj
    const announced = this.mutating
    this.mutating = false
    // A plan swap (`changes`) re-keys its line without adding or removing anything.
    if (!announced || changes) return

    const added = []
    const removed = []
    next.forEach((item, key) => {
      const delta = item.quantity - (previous.get(key)?.quantity || 0)
      if (delta > 0) added.push(this.toItem(item, delta))
      if (delta < 0) removed.push(this.toItem(item, -delta))
    })
    previous.forEach((item, key) => {
      if (!next.has(key)) removed.push(this.toItem(item, item.quantity))
    })

    if (added.length) {
      const value = Number(added.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2))
      this.pushEcommerce('add_to_cart', { value, items: added })
    }
    if (removed.length) this.pushEcommerce('remove_from_cart', { items: removed })
  }

  /* ---- view_cart / begin_checkout ----------------------------------------- */

  handleDrawerOpened(event) {
    if (event.detail?.drawerId !== this.cartDrawerId) return
    this.pushEcommerce('view_cart', { items: this.lineItems(this.cart) })
  }

  handleCheckout(event) {
    this.pushEcommerce('begin_checkout', { items: this.lineItems(event.detail?.cartObj || this.cart) })
  }

  /* ---- content interactions ----------------------------------------------- */

  handleMediaChange(event) {
    this.push({ event: 'view_product_image', product_image_number: event.detail?.position })
  }

  handleComparison(event) {
    const { sectionName, label } = event.detail || {}
    this.push({ event: 'image_comparison_interaction', sectionName: sectionName || label || 'Unknown', tabLabel: label })
  }
}

if (!customElements.get('ia-ga4-events')) {
  customElements.define('ia-ga4-events', IaGa4Events)
}
